/**
 * PublishingSection Component - Shopify Sales Channel Requirement
 *
 * Displays:
 * - Number of products published to InClub
 * - Link to Shopify bulk product editor
 * - Products with publishing errors
 *
 * Uses Polaris Layout.AnnotatedSection as required by Shopify
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  Button,
  Link,
  Spinner,
  ProgressBar
} from '@shopify/polaris';

export default function PublishingSection({ shopDomain, inclubId, authenticatedRequest }) {
  const [stats, setStats] = useState(null);
  const [collectionStats, setCollectionStats] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const syncPollRef = useRef(null);

  useEffect(() => {
    loadPublishingData();
    loadCollectionData();
    loadSyncStatus();
  }, [inclubId]);

  // Start/stop polling based on sync status
  useEffect(() => {
    // Clear any existing interval
    if (syncPollRef.current) {
      clearInterval(syncPollRef.current);
      syncPollRef.current = null;
    }

    // Only poll if currently syncing
    if (syncStatus?.isSyncing) {
      syncPollRef.current = setInterval(() => {
        loadSyncStatus();
      }, 2000); // Poll every 2 seconds while syncing
    }

    return () => {
      if (syncPollRef.current) {
        clearInterval(syncPollRef.current);
      }
    };
  }, [syncStatus?.isSyncing]);

  // Reload data when syncing completes
  useEffect(() => {
    if (syncStatus && !syncStatus.isSyncing) {
      if (syncStatus.products.processed > 0) {
        loadPublishingData();
      }
      if (syncStatus.collections.processed > 0) {
        loadCollectionData();
      }
    }
  }, [syncStatus?.isSyncing]);

  const loadSyncStatus = async () => {
    try {
      const response = await authenticatedRequest('GET', '/sync-status');
      if (response.success) {
        setSyncStatus(response.data);
      }
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  };

  const loadCollectionData = async () => {
    if (!inclubId) return;

    try {
      const response = await authenticatedRequest('GET', `/collection-stats/${inclubId}`);
      if (response.success) {
        setCollectionStats(response.stats);
      }
    } catch (error) {
      console.error('Failed to load collection stats:', error);
    }
  };

  const loadPublishingData = async () => {
    if (!inclubId) {
      setLoading(false);
      return;
    }

    try {
      setLoadError(null);
      const response = await authenticatedRequest('GET', `/publishing-stats/${inclubId}`);

      if (response.success) {
        setStats(response.stats);
        setErrors(response.errors || []);
      } else {
        setLoadError(response.error || 'Failed to load publishing stats');
      }
    } catch (error) {
      console.error('Failed to load publishing stats:', error);
      setLoadError(error.message || 'Failed to load publishing stats');
    } finally {
      setLoading(false);
    }
  };

  // Resync all products and collections from Shopify
  const handleResyncAll = async () => {
    if (!inclubId || resyncing) return;

    try {
      setResyncing(true);
      setResyncResult(null);

      // Start polling immediately to show progress
      loadSyncStatus();
      if (!syncPollRef.current) {
        syncPollRef.current = setInterval(() => {
          loadSyncStatus();
        }, 2000);
      }

      const [productResult, collectionResult] = await Promise.allSettled([
        authenticatedRequest('POST', `/resync-products/${inclubId}`),
        authenticatedRequest('POST', `/resync-collections/${inclubId}`)
      ]);

      const messages = [];
      let hasError = false;

      if (productResult.status === 'fulfilled' && productResult.value.success) {
        if (productResult.value.async) {
          messages.push('Product sync started');
        } else {
          const s = productResult.value.stats;
          messages.push(`Synced ${s.synced} products, removed ${s.removed} stale`);
        }
      } else {
        hasError = true;
        const err = productResult.status === 'rejected'
          ? productResult.reason?.message
          : productResult.value?.error;
        messages.push(`Products: ${err || 'failed'}`);
      }

      if (collectionResult.status === 'fulfilled' && collectionResult.value.success) {
        if (collectionResult.value.async) {
          messages.push('Collection sync started');
        } else {
          const s = collectionResult.value.stats;
          messages.push(`Synced ${s.synced} collections, removed ${s.removed} stale`);
        }
      } else {
        hasError = true;
        const err = collectionResult.status === 'rejected'
          ? collectionResult.reason?.message
          : collectionResult.value?.error;
        messages.push(`Collections: ${err || 'failed'}`);
      }

      setResyncResult({
        success: !hasError,
        message: messages.join('. ')
      });

      // Reload stats after resync
      await Promise.all([loadPublishingData(), loadCollectionData()]);
    } catch (error) {
      console.error('Failed to resync:', error);
      setResyncResult({
        success: false,
        message: error.message || 'Failed to resync'
      });
    } finally {
      setResyncing(false);
    }
  };

  // Link to Shopify product management
  const bulkEditorUrl = `https://${shopDomain}/admin/products`;
  const collectionsUrl = `https://${shopDomain}/admin/collections`;

  if (loading) {
    return (
      <Layout.AnnotatedSection
        title="Product Publishing"
        description="Manage products available on InClub."
      >
        <Card>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Spinner size="small" />
            <div style={{ marginTop: '12px' }}>
              <Text variant="bodyMd" tone="subdued">Loading publishing stats...</Text>
            </div>
          </div>
        </Card>
      </Layout.AnnotatedSection>
    );
  }

  if (loadError) {
    return (
      <Layout.AnnotatedSection
        title="Product Publishing"
        description="Manage products available on InClub."
      >
        <Card>
          <Banner tone="warning" title="Unable to load publishing stats">
            <p>{loadError}</p>
            <div style={{ marginTop: '12px' }}>
              <Button onClick={loadPublishingData}>Retry</Button>
            </div>
          </Banner>
        </Card>
      </Layout.AnnotatedSection>
    );
  }

  return (
    <Layout.AnnotatedSection
      title="Product Publishing"
      description="Manage which products are available on the InClub sales channel. Products must be published to InClub from your Shopify admin."
    >
      <Card>
        <BlockStack gap="4">
          {/* Sync Status Banner - Shows when webhooks are being processed */}
          {syncStatus?.isSyncing && (
            <Banner tone="info">
              <BlockStack gap="2">
                <InlineStack gap="2" blockAlign="center">
                  <Spinner size="small" />
                  <Text variant="bodyMd" fontWeight="semibold">
                    Importing products from Shopify...
                  </Text>
                </InlineStack>
                <BlockStack gap="1">
                  {syncStatus.products.pending > 0 && (
                    <Text variant="bodyMd">
                      {syncStatus.products.pending} product{syncStatus.products.pending !== 1 ? 's' : ''} pending
                      {syncStatus.products.processed > 0 && ` (${syncStatus.products.processed} processed)`}
                    </Text>
                  )}
                  {syncStatus.collections.pending > 0 && (
                    <Text variant="bodyMd">
                      {syncStatus.collections.pending} collection{syncStatus.collections.pending !== 1 ? 's' : ''} pending
                      {syncStatus.collections.processed > 0 && ` (${syncStatus.collections.processed} processed)`}
                    </Text>
                  )}
                </BlockStack>
                {(syncStatus.products.pending + syncStatus.products.processed) > 0 && (
                  <ProgressBar
                    progress={Math.round(
                      (syncStatus.products.processed /
                        (syncStatus.products.pending + syncStatus.products.processed)) *
                        100
                    )}
                    size="small"
                  />
                )}
              </BlockStack>
            </Banner>
          )}

          {/* Resync All + Manage Links */}
          <InlineStack gap="4" align="end">
            <Button
              onClick={handleResyncAll}
              loading={resyncing}
              disabled={resyncing || syncStatus?.isSyncing}
            >
              {resyncing ? 'Syncing...' : 'Resync All'}
            </Button>
            <Button url={bulkEditorUrl} external>
              Manage Products
            </Button>
            <Button url={collectionsUrl} external>
              Manage Collections
            </Button>
          </InlineStack>

          {/* Resync Result Banner */}
          {resyncResult && (
            <Banner
              tone={resyncResult.success ? 'success' : 'critical'}
              onDismiss={() => setResyncResult(null)}
            >
              <p>{resyncResult.message}</p>
            </Banner>
          )}

          {/* Product Stats */}
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="1">
              <Text variant="headingXl" as="p">
                {stats?.publishedCount || 0}
              </Text>
              <Text variant="bodyMd" tone="subdued">
                products published to InClub
              </Text>
            </BlockStack>
          </InlineStack>

          {/* Available for sale info */}
          {stats?.publishedCount > 0 && (
            <Text variant="bodyMd" tone="subdued">
              {stats?.availableCount || 0} of {stats?.publishedCount || 0} products available for sale
            </Text>
          )}

          {/* Error Banner */}
          {errors.length > 0 && (
            <Banner
              title={`${errors.length} product${errors.length > 1 ? 's have' : ' has'} publishing issues`}
              tone="warning"
            >
              <p>Some products cannot be displayed on InClub. Please fix the issues below.</p>
            </Banner>
          )}

          {/* Error List */}
          {errors.length > 0 && (
            <BlockStack gap="2">
              <Text variant="headingMd">Publishing Issues</Text>
              {errors.slice(0, 5).map((error, index) => (
                <InlineStack key={index} gap="2" align="start">
                  <Link
                    url={`https://${shopDomain}/admin/products/${error.productId}`}
                    external
                    monochrome
                  >
                    {error.productTitle}
                  </Link>
                  <Text tone="critical"> - {error.message}</Text>
                </InlineStack>
              ))}
              {errors.length > 5 && (
                <Text tone="subdued">
                  And {errors.length - 5} more...
                </Text>
              )}
            </BlockStack>
          )}

          {/* Empty state */}
          {stats?.publishedCount === 0 && (
            <Banner tone="info">
              <p>
                No products are published to InClub yet. Go to your{' '}
                <Link url={bulkEditorUrl} external>
                  Products page
                </Link>{' '}
                and add InClub as a sales channel for the products you want to sell.
              </p>
            </Banner>
          )}
        </BlockStack>
      </Card>

      {/* Collections Section */}
      <Card>
        <BlockStack gap="4">
          {/* Collection Sync Status Banner */}
          {syncStatus?.collections?.pending > 0 && (
            <Banner tone="info">
              <InlineStack gap="2" blockAlign="center">
                <Spinner size="small" />
                <Text variant="bodyMd">
                  Importing {syncStatus.collections.pending} collection{syncStatus.collections.pending !== 1 ? 's' : ''}...
                  {syncStatus.collections.processed > 0 && ` (${syncStatus.collections.processed} processed)`}
                </Text>
              </InlineStack>
            </Banner>
          )}

          {/* Collection Stats Row */}
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="1">
              <Text variant="headingXl" as="p">
                {collectionStats?.totalCount || 0}
              </Text>
              <Text variant="bodyMd" tone="subdued">
                collections published to InClub
              </Text>
            </BlockStack>
          </InlineStack>

          {/* Empty state for collections */}
          {collectionStats?.totalCount === 0 && (
            <Banner tone="info">
              <p>
                No collections are published to InClub yet. Go to your{' '}
                <Link url={collectionsUrl} external>
                  Collections page
                </Link>{' '}
                and add InClub as a sales channel for the collections you want to display.
              </p>
            </Banner>
          )}
        </BlockStack>
      </Card>
    </Layout.AnnotatedSection>
  );
}
